import { api, type ApiOpts } from '@/lib/api';
import { getKidSub, useAuthStore } from '@/auth/authStore';
import { workingContentSchema } from './workingCanvas';

async function request<T>(path: string, options: ApiOpts = {}): Promise<T> {
  const owner = getKidSub();
  if (!owner) throw new Error('Sign in again to open your notes.');
  const controller = new AbortController();
  const unsubscribe = useAuthStore.subscribe(() => {
    if (getKidSub() !== owner) controller.abort();
  });
  try {
    const result = await api<T>(path, { ...options, signal: controller.signal });
    if (controller.signal.aborted || getKidSub() !== owner)
      throw new Error('Your account changed. Reopen your notes.');
    return result;
  } finally {
    unsubscribe();
  }
}
export type NoteOperationKind =
  | 'knowledge'
  | 'simplify'
  | 'expand'
  | 'rewrite'
  | 'region'
  | 'flashcards'
  | 'quiz'
  | 'audio';
export interface NoteRegion {
  document_id: string;
  page_number: number;
  x: number;
  y: number;
  width: number;
  height: number;
}
export interface NoteOperationSelection {
  expected_version: number;
  source?: 'saved_notes' | 'original_material';
  working?: { id: string; version: number };
  annotations?: NoteAnnotationSelection[];
  knowledge_ids?: string[];
  from?: number;
  to?: number;
  instruction?: string;
  region?: NoteRegion;
}
export interface NoteAnnotationSelection {
  id: string;
  version: number;
  document_id: string;
  source_revision: string;
}
export interface StudyFlashcard {
  front: string;
  back: string;
  evidence: string;
  working_evidence?: { tile: number; quote: string };
}
export { request as requestStudyNoteLearning };
export interface NoteOperation {
  id: string;
  notebook_id: string;
  kind: NoteOperationKind;
  status:
    | 'queued'
    | 'running'
    | 'awaiting_approval'
    | 'ready'
    | 'failed'
    | 'refund_pending'
    | 'expired';
  source_version: number;
  stale: boolean;
  source?: 'saved_notes' | 'original_material';
  working?: { id: string; version: number } | null;
  annotations?: (NoteAnnotationSelection & { page_number: number })[];
  revision: number;
  stars_charged: number;
  stars_refunded: number;
  error_code: string | null;
  result: {
    text?: string;
    cards?: StudyFlashcard[];
    script?: string;
    working_coverage?: {
      status: 'included' | 'partly_unreadable' | 'unreadable';
      omissions: string[];
    };
    analysis_id?: string;
    reviews?: Record<string, { rating: string; due_at: string }>;
  } | null;
  practice_set_id: string | null;
  audio: { url: string; expires_in: number } | null;
}
const path = (id: string) => `/study/notes/${encodeURIComponent(id)}/operations`;
export const noteOperations = {
  working: async (documentId: string) => {
    const row = await request<{ id: string; version: number; content: unknown } | null>(
      `/study/working-canvases/draft?kind=material&question_id=${encodeURIComponent(documentId)}`,
    );
    if (!row) return null;
    const content = workingContentSchema.parse(row.content);
    return {
      id: row.id,
      version: row.version,
      stroke_count: content.strokes.length,
      text_characters: content.text.length,
    };
  },
  list: (id: string) => request<{ items: NoteOperation[] }>(path(id)),
  get: (id: string, operationId: string) =>
    request<NoteOperation>(`${path(id)}/${encodeURIComponent(operationId)}`),
  quote: (id: string, kind: NoteOperationKind, selection: NoteOperationSelection) =>
    request<{ stars: number }>(`${path(id)}/quote/${kind}`, { method: 'POST', body: selection }),
  create: (
    id: string,
    kind: NoteOperationKind,
    selection: NoteOperationSelection,
    stars: number,
    key: string,
  ) =>
    request<NoteOperation>(path(id), {
      method: 'POST',
      body: { kind, selection, expected_stars: stars, idempotency_key: key },
    }),
  change: <T>(
    id: string,
    operation: string,
    action: 'apply' | 'undo' | 'cards' | 'cancel' | 'audio-approval' | 'audio-draft',
    body: unknown = {},
  ) => request<T>(`${path(id)}/${operation}/${action}`, { method: 'POST', body }),
};
